/*
Tactile + visual confirmations for the capture flow.

Two distinct moments since Android v1.0.1 *Vérité & Friction* split
the feedback into a tap-side and a success-side beat:
- `triggerCaptureTapHaptic()`: short LongPress-style buzz the instant
  the user taps "📷 Capturer", BEFORE the camera opens. Confirms the
  tap was registered while the iOS file-picker is still spinning up.
- `triggerCaptureSuccessFlash()`: 220 ms full-screen white wash when
  the IDB INSERT lands. Doubles as a visual shutter on platforms
  where vibration isn't supported (iOS Safari).
- `triggerCaptureCancelTic()`: very short pulse + soft confirmation
  when the iOS camera app is dismissed without taking a photo.

The flash element is appended directly to `<body>` (not `#app`) so
it survives the router's per-screen `innerHTML = ''` if a navigation
happens to fire on the same tick. It removes itself on
`transitionend`, with a setTimeout fallback for the rare case
transitionend gets coalesced away by the browser.
*/

const kFlashDurationMs: number = 240   // matches the CSS transition; cleanup fallback runs at +60 ms
const kHapticTapMs: number = 20        // LongPress-style tap haptic, fires on the Capturer button BEFORE the camera opens
const kHapticCancelMs: number = 10     // even shorter tic when the user dismisses the camera

/*
Tap haptic — fire on the Capturer button click. Cheap; safe to call
from a user-gesture handler. Mirrors Android's "haptique déplacée du
post-INSERT vers le tap Capturer (perçu immédiat avant l'ouverture
caméra)".
*/
export function triggerCaptureTapHaptic(): void
{
	vibrate(kHapticTapMs)
}

/*
Success flash — fire once the IDB INSERT confirms. The visual is
universal; the haptic part has moved to triggerCaptureTapHaptic.
*/
export function triggerCaptureSuccessFlash(): void
{
	flashScreen()
}

/*
Cancel tic — fire when the camera app is dismissed without a photo.
Pairs with the MapScreen toast "Capture annulée" for non-blocking
feedback that the tap was registered but nothing was saved.
*/
export function triggerCaptureCancelTic(): void
{
	vibrate(kHapticCancelMs)
}

/*
One-shot vibration. Browser support is patchy: Android Chrome / Edge
respect it, iOS Safari + Firefox iOS ignore it silently. We don't
guard for the iOS case beyond the API existence check — the no-op
is the desired fallback.
*/
function vibrate(inDurationMs: number): void
{
	if (typeof navigator === 'undefined') return
	if (typeof navigator.vibrate !== 'function') return
	try { navigator.vibrate(inDurationMs) }
	catch { /* iOS may throw "NotSupportedError" inside a sandboxed standalone PWA — non-fatal */ }
}

/*
Visual shutter — append a fixed-position white wash, force a paint
at full opacity, then transition to 0 in the next frame. Removes
itself when the transition ends.
*/
function flashScreen(): void
{
	if (typeof document === 'undefined') return
	const vEl = document.createElement('div')
	vEl.className = 'capture-flash'
	document.body.appendChild(vEl)

	let vRemoved = false  // single-fire guard for the cleanup path
	const vCleanup = (): void => {
		if (vRemoved) return
		vRemoved = true
		vEl.remove()
	}
	vEl.addEventListener('transitionend', vCleanup, { once: true })
	// Fallback: if the browser coalesces the transitionend (common when
	// the page is hidden mid-animation) we still want the element gone.
	window.setTimeout(vCleanup, kFlashDurationMs + 60)

	// Two-frame trick: the element is inserted at opacity 1 (CSS default
	// in `.capture-flash`); we wait for one paint, then add the
	// `-fading` class which transitions opacity to 0. Without the rAF
	// the browser collapses both states into the final one and skips
	// the transition entirely.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => vEl.classList.add('capture-flash-fading'))
	})
}
